import type { Tick } from "../types";

type Handler = (ticks: Tick[]) => void;
export type RelayStatus = "connecting" | "connected" | "disconnected" | "error";

// Browser side of the backend tick relay (/api/ws/ticks). One socket, many symbols. JWT from localStorage.
class TickRelay {
  private ws: WebSocket | null = null;
  private handlers = new Map<string, Set<Handler>>();
  private connecting = false;
  private failures = 0;
  available = true;
  status: RelayStatus = "connecting";
  private statusListeners = new Set<(s: RelayStatus) => void>();
  private reconnectListeners = new Set<() => void>();

  onStatus(cb: (s: RelayStatus) => void): () => void { this.statusListeners.add(cb); cb(this.status); return () => this.statusListeners.delete(cb); }
  onReconnect(cb: () => void): () => void { this.reconnectListeners.add(cb); return () => this.reconnectListeners.delete(cb); }
  private setStatus(s: RelayStatus) { if (this.status !== s) { this.status = s; this.statusListeners.forEach((cb) => cb(s)); } }

  private url(): string | null {
    const base = process.env.REACT_APP_BACKEND_URL;
    const token = localStorage.getItem("felacoo_token");
    if (!base || !token) return null;
    return `${base.replace(/^http/, "ws")}/api/ws/ticks?token=${encodeURIComponent(token)}`;
  }

  private connect() {
    if (this.ws || this.connecting) return;
    const url = this.url();
    if (!url) { this.available = false; this.setStatus("error"); return; }
    this.connecting = true;
    this.setStatus("connecting");
    const ws = new WebSocket(url);
    ws.onopen = () => {
      const reconnected = this.failures > 0;
      this.connecting = false; this.failures = 0; this.ws = ws; this.available = true;
      this.setStatus("connected");
      for (const sym of this.handlers.keys()) ws.send(JSON.stringify({ subscribe: sym }));
      if (reconnected) this.reconnectListeners.forEach((cb) => cb());
    };
    ws.onmessage = (e) => {
      let m: any; try { m = JSON.parse(e.data); } catch { return; }
      if (m.type === "ticks") this.handlers.get(m.symbol)?.forEach((h) => h(m.ticks));
    };
    ws.onclose = () => {
      this.connecting = false; this.ws = null; this.failures++;
      this.setStatus("disconnected");
      if (this.failures >= 3) this.available = false;
      if (this.handlers.size) setTimeout(() => this.connect(), Math.min(10000, 2000 * this.failures));
    };
    ws.onerror = () => ws.close();
  }

  subscribe(symbol: string, cb: Handler): () => void {
    if (!this.handlers.has(symbol)) this.handlers.set(symbol, new Set());
    this.handlers.get(symbol)!.add(cb);
    if (this.ws?.readyState === 1) this.ws.send(JSON.stringify({ subscribe: symbol }));
    else this.connect();
    return () => {
      const set = this.handlers.get(symbol);
      set?.delete(cb);
      if (set && set.size === 0) {
        this.handlers.delete(symbol);
        if (this.ws?.readyState === 1) this.ws.send(JSON.stringify({ unsubscribe: symbol }));
      }
    };
  }
}

let instance: TickRelay | null = null;
export function getRelay(): TickRelay {
  if (!instance) instance = new TickRelay();
  return instance;
}
